<template>
  <div class="nft-gallery">
    <banner :banner="bannerData" />
    <section class="gallery-content">
      <header class="gallery-heading">
        <h1>NFTs</h1>
        <span v-if="total">{{ total }} items</span>
      </header>

      <div v-if="items.length" class="gallery-grid">
        <router-link
          v-for="nft in items"
          :key="nft.id"
          :to="{ name: 'marketNftDetail', params: { id: nft.id } }"
          class="gallery-card"
        >
          <img v-src="nft.imageUrl || placeholder" :alt="nft.name" />
          <div class="card-details">
            <h2>{{ nft.name }}</h2>
            <p>{{ nft.collectionName }} · #{{ nft.tokenId }}</p>
            <small>{{ nft.chainName }}</small>
          </div>
        </router-link>
      </div>

      <p v-else-if="loaded && !error" class="empty-state">
        {{ $t('common.noData') }}
      </p>
      <p v-if="error" class="error-state">{{ error }}</p>

      <button
        v-if="items.length < total && !loading"
        class="load-more"
        type="button"
        @click="loadMore"
      >
        Load more
      </button>
      <p v-if="loading" class="empty-state">Loading…</p>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import banner from '@/components/market/banner.vue';
import { getOnchainNftGallery } from '@/api/nft.js';

const pageSize = 60;
const placeholder = require('@/assets/market/placeholder.png');
const items = ref([]);
const total = ref(0);
const loading = ref(false);
const loaded = ref(false);
const error = ref('');
const bannerData = {
  title: 'NFTs',
  background: require('@/assets/banner.png'),
};

const loadMore = async () => {
  if (loading.value || items.value.length >= total.value && loaded.value) return;
  loading.value = true;
  error.value = '';
  try {
    const { data: response } = await getOnchainNftGallery({
      offset: items.value.length,
      limit: pageSize,
    });
    const page = response.result;
    items.value.push(...page);
    total.value = response.pagination.total;
    loaded.value = true;
  } catch (err) {
    error.value = 'NFT list could not be loaded.';
  } finally {
    loading.value = false;
  }
};

onMounted(loadMore);
</script>

<style scoped lang="less">
.gallery-content {
  padding: 3rem 10% 6rem;
  background: #ecf1f9;
}
.gallery-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  color: #183b56;
  h1 {
    margin: 0;
    font-size: 2.6rem;
  }
  span {
    color: #6f7d95;
    font-size: 1.5rem;
  }
}
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.8rem;
}
.gallery-card {
  display: block;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
  color: inherit;
  text-decoration: none;
  box-shadow: 0 2px 8px rgb(15 45 68 / 8%);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgb(15 45 68 / 14%);
  }
  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    background: #f7f8fb;
  }
}
.card-details {
  padding: 1.3rem 1.5rem 1.6rem;
  color: #183b56;
  h2 {
    overflow: hidden;
    margin: 0 0 0.6rem;
    font-size: 1.6rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  p,
  small {
    display: block;
    overflow: hidden;
    margin: 0;
    color: #6f7d95;
    font-size: 1.3rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  small {
    margin-top: 0.5rem;
  }
}
.empty-state,
.error-state {
  padding: 3rem 0;
  color: #6f7d95;
  text-align: center;
  font-size: 1.5rem;
}
.error-state {
  color: #c0392b;
}
.load-more {
  display: block;
  margin: 2.5rem auto 0;
  padding: 1rem 2rem;
  border: 0;
  border-radius: 2rem;
  background: #566ffe;
  color: #fff;
  cursor: pointer;
  font-size: 1.5rem;
}
@media screen and (max-width: 1000px) {
  .gallery-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media screen and (max-width: 700px) {
  .gallery-content {
    padding: 2rem 5% 4rem;
  }
  .gallery-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
}
@media screen and (max-width: 390px) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}
</style>
